import { useState, useMemo, useCallback } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useWithdrawFromPayoutAccountMutation } from '@graphql/mutations/registry.graphql.generated';
import { RegistryPayoutAccountExternalAccountPayoutMethod } from '@graphql/generated-base';
import type { PayoutAccountInfo } from '../CreditCardSetUp.context';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import type { PayoutStatus, PayoutDialogStep } from '@shared/components/PayoutDialog/PayoutDialog.types';
import { useFeatureValue } from '@shared/core/featureFlags';
import { getTransferPartnerConfig } from '@shared/components/TransferToBankDialog/transferPartnerConfigs';

export const usePayoutDialogController = (fetchPayoutInfo: () => void, payoutAccountInfo: PayoutAccountInfo | null, eventId: string) => {
  const telemetry = useCreditCardSetupTelemetry();
  const [status, setStatus] = useState<PayoutStatus>('pending');
  const isReadyToUse = payoutAccountInfo?.status === 'readyToUse';

  const availableInMinorUnits = payoutAccountInfo?.balance?.available.valueInMinorUnits ?? 0;
  const isPayoutAllowed = availableInMinorUnits > 0;

  const { value: activePartner } = useFeatureValue('activeCashFundPartner');
  const { value: transferPartner } = useFeatureValue('enableCashFundPartnerTransferToBank');
  const { value: repeatTransferPartner } = useFeatureValue('enableCashFundPartnerTransferToBankRepeat');
  const hasActiveInitialPromo = Boolean(!isReadyToUse && activePartner && activePartner === transferPartner && getTransferPartnerConfig(activePartner));
  const hasActiveRepeatPromo = Boolean(isReadyToUse && isPayoutAllowed && activePartner && activePartner === repeatTransferPartner && getTransferPartnerConfig(activePartner));
  const hasActivePromo = hasActiveInitialPromo || hasActiveRepeatPromo;

  const isThrottled = useCallback(
    () => Boolean(payoutAccountInfo?.nextWithdrawalAvailableAt && payoutAccountInfo.nextWithdrawalAvailableAt.milliseconds > Date.now()),
    [payoutAccountInfo],
  );
  const getInitialStep = (): PayoutDialogStep => {
    if (!isReadyToUse || hasActivePromo) return 'connectBank';
    if (isThrottled()) return 'restricted';
    return 'amount';
  };
  const [dialogStep, setDialogStep] = useState<PayoutDialogStep>(getInitialStep);
  const [selectedMethod, setSelectedMethod] = useState<RegistryPayoutAccountExternalAccountPayoutMethod>(RegistryPayoutAccountExternalAccountPayoutMethod.standard);

  const maxAmountInMinorUnits = useMemo(() => {
    if (payoutAccountInfo?.payoutLimitInMinorUnits != null) {
      return Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits);
    }
    return availableInMinorUnits;
  }, [payoutAccountInfo?.payoutLimitInMinorUnits, availableInMinorUnits]);

  const [amountInMinorUnits, setAmountInMinorUnits] = useState<number>(() => maxAmountInMinorUnits);

  const payoutAccountAllowed = useMemo(() => Boolean(payoutAccountInfo?.balance && payoutAccountInfo.balance.available.valueInMinorUnits > 0), [payoutAccountInfo]);

  const { isOpen: isOpenPayoutDialog, onOpen: onOpenPayoutDialog, onClose } = useDisclosure();

  const [withdrawBalance, { loading: loadingPayoutRequest }] = useWithdrawFromPayoutAccountMutation({
    variables: {
      eventId,
      payoutMethod: selectedMethod,
      amountInMinorUnits,
    },
    onCompleted: response => {
      const withdrawal = response.withdrawFromPayoutAccount;
      if (withdrawal.status !== 'failed') {
        telemetry.payoutDialog.enter(withdrawal.status);
        fetchPayoutInfo();
        setStatus('success');
        setDialogStep('confirmation');
      } else {
        telemetry.payoutDialog.enter(withdrawal.status);
        setStatus('error');
        setDialogStep('confirmation');
      }
    },
    onError: () => {
      telemetry.payoutDialog.enter('error');
      setStatus('error');
      setDialogStep('confirmation');
    },
  });

  const handleTransfer = useCallback(() => {
    withdrawBalance();
  }, [withdrawBalance]);

  // Promise-returning variant for PartnerTransferSheet, whose controller submits with an
  // explicit amount + method rather than reading them off this controller's own state (there's no
  // separate "amount" / "method" step in the new sheet to set them first). Reuses the same
  // mutate function as handleTransfer, so the hook-level onCompleted/onError above -- telemetry,
  // fetchPayoutInfo refetch, dialogStep/status bookkeeping -- still run unchanged.
  const withdraw = useCallback(
    async (amount: number, method: RegistryPayoutAccountExternalAccountPayoutMethod): Promise<'ok' | 'failed' | 'unknown'> => {
      // Only a status from the server says what happened. No status -- a dropped connection, a
      // request error -- means the withdrawal may or may not have gone through, so the sheet must
      // not tell the couple their balance is unchanged. Refresh the balance so whatever they look
      // at next is true (the hook-level onError does not).
      try {
        const result = await withdrawBalance({ variables: { eventId, payoutMethod: method, amountInMinorUnits: amount } });
        const withdrawalStatus = result.data?.withdrawFromPayoutAccount.status;
        if (withdrawalStatus) return withdrawalStatus !== 'failed' ? 'ok' : 'failed';
      } catch {
        // falls through to the unknown outcome below
      }
      fetchPayoutInfo();
      return 'unknown';
    },
    [withdrawBalance, eventId, fetchPayoutInfo],
  );

  const onContinueToMethod = useCallback(() => {
    setDialogStep('method');
  }, []);

  const onContinueToAmount = useCallback(() => {
    setDialogStep('amount');
  }, []);

  const onBackToAmount = useCallback(() => {
    setDialogStep('amount');
  }, []);

  const onCloseDialog = useCallback(() => {
    setStatus('pending');
    setDialogStep(!isReadyToUse || hasActivePromo ? 'connectBank' : isThrottled() ? 'restricted' : 'amount');
    setSelectedMethod(RegistryPayoutAccountExternalAccountPayoutMethod.standard);
    setAmountInMinorUnits(maxAmountInMinorUnits);
    onClose();
  }, [maxAmountInMinorUnits, onClose, isReadyToUse, hasActivePromo, isThrottled]);

  const onOpenDialog = useCallback(() => {
    const newMax = payoutAccountInfo?.payoutLimitInMinorUnits != null ? Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits) : availableInMinorUnits;
    setAmountInMinorUnits(newMax);
    setDialogStep(!isReadyToUse || hasActivePromo ? 'connectBank' : isThrottled() ? 'restricted' : 'amount');
    setStatus('pending');
    setSelectedMethod(RegistryPayoutAccountExternalAccountPayoutMethod.standard);
    onOpenPayoutDialog();
  }, [payoutAccountInfo, availableInMinorUnits, onOpenPayoutDialog, isReadyToUse, hasActivePromo, isThrottled]);

  const resetPayoutStatus = useCallback(() => {
    setStatus('pending');
  }, []);

  return {
    handleTransfer,
    withdraw,
    loadingPayoutRequest,
    payoutAccountAllowed,
    isOpenPayoutDialog,
    onOpenPayoutDialog: onOpenDialog,
    onClosePayoutDialog: onCloseDialog,
    resetPayoutStatus,
    payoutStatus: status,
    accountNumber: payoutAccountInfo?.externalAccountLast4 || '',
    dialogStep,
    amountInMinorUnits,
    onAmountChange: setAmountInMinorUnits,
    onContinueToMethod,
    onContinueToAmount,
    onBackToAmount,
    selectedMethod,
    onMethodChange: setSelectedMethod,
    isPayoutAllowed,
  };
};
